
import React from "react";
import { Helmet } from "react-helmet-async";
import Layout from "../components/Layout";
import { ArrowRight, Monitor, Cpu, Thermometer, Award, Zap, HardDrive, Gamepad2, Briefcase, Palette, Code } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const ProductPC = () => {
  const navigate = useNavigate();

  const handleRequestQuote = () => {
    navigate('/', { state: { scrollToContact: true } });
  };

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    'name': 'Custom PCs & Workstations - 25+ Years Manufacturing Excellence',
    'image': [
      'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=600&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1587831990711-23ca6441447b?w=600&auto=format&fit=crop'
    ],
    'description': 'High-performance custom PCs and workstations. Premium components, custom cooling solutions, and professional warranty for business and design needs.',
    'brand': {
      '@type': 'Brand',
      'name': 'Truecore Gadgets',
      'foundingDate': '2000'
    },
    'offers': {
      '@type': 'Offer',
      'price': '65000',
      'priceCurrency': 'INR',
      'priceValidUntil': '2025-12-31',
      'availability': 'https://schema.org/InStock'
    },
    'aggregateRating': {
      '@type': 'AggregateRating',
      'ratingValue': '4.9',
      'reviewCount': '87',
      'bestRating': '5',
      'worstRating': '1'
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Custom PCs India | Business & Gaming Workstations | Truecore</title>
        <meta name="description" content="Buy premium custom PCs in India built for design, business, and gaming. Trusted electronics manufacturer with premium components, professional cooling, and comprehensive warranty." />
        <meta name="keywords" content="Custom PCs India, Desktop computers for business, Professional Workstations, High-Performance Computing, Gaming PCs, Design Workstations, Trusted gadget brand India, Top electronics brand 2025" />
        
        <link rel="canonical" href="https://truecoregadgets.com/products/pcs" />
        
        <meta property="og:type" content="product" />
        <meta property="og:title" content="Custom PCs India | Business & Gaming Workstations" />
        <meta property="og:description" content="Buy high-performance custom PCs built for design, business & gaming with premium components and professional cooling." />
        <meta property="og:url" content="https://truecoregadgets.com/products/pcs" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=600&auto=format&fit=crop" />
        
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      </Helmet>

      <section className="container mx-auto py-16 px-4 max-w-6xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6 gradient-text">Custom PCs & Workstations - Built for Performance</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            From gaming rigs to professional workstations - we build PCs that deliver uncompromising performance with premium components and expert craftsmanship.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground mb-8">
            <span className="flex items-center"><Cpu className="h-4 w-4 mr-2 text-primary" />Latest Processors</span>
            <span className="flex items-center"><Thermometer className="h-4 w-4 mr-2 text-primary" />Custom Cooling</span>
            <span className="flex items-center"><Award className="h-4 w-4 mr-2 text-primary" />5-Year Warranty</span>
            <span className="flex items-center"><Zap className="h-4 w-4 mr-2 text-primary" />Fast Delivery</span>
          </div>
        </div>

        {/* Main Product Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div>
            <img
              src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=600&auto=format&fit=crop"
              className="rounded-xl shadow-lg w-full object-cover"
              alt="Custom PCs and Workstations by Truecore - Premium Computers for Business"
              loading="lazy"
            />
          </div>
          
          <div>
            <h2 className="text-3xl font-bold mb-6">Why Choose Custom Over Off-the-Shelf?</h2>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Cpu className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Premium Component Selection</h3>
                  <p className="text-muted-foreground">Hand-picked components from trusted brands - no compromises on quality or performance.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Thermometer className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Advanced Thermal Management</h3>
                  <p className="text-muted-foreground">Custom cooling solutions keep your system running cool and quiet even under heavy loads.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Monitor className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Tailored Configuration</h3>
                  <p className="text-muted-foreground">Built specifically for your workflow - whether gaming, content creation, or business applications.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Award className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Extended Warranty & Support</h3>
                  <p className="text-muted-foreground">5-year comprehensive warranty with expert technical support and upgrade services.</p>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <Button 
                onClick={handleRequestQuote}
                className="w-full sm:w-auto flex items-center justify-center"
                size="lg"
              >
                Request Custom Quote
                <ArrowRight className="ml-2" />
              </Button>
            </div>
          </div>
        </div>

        {/* Detailed Tabs Section */}
        <Tabs defaultValue="configurations" className="mb-16">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="configurations">PC Types</TabsTrigger>
            <TabsTrigger value="specifications">Specifications</TabsTrigger>
            <TabsTrigger value="pricing">Pricing</TabsTrigger>
            <TabsTrigger value="services">Services</TabsTrigger>
          </TabsList>
          
          <TabsContent value="configurations" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-card p-6 rounded-lg border">
                <Gamepad2 className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-3">Gaming PCs</h3>
                <p className="text-muted-foreground mb-4">High-performance gaming rigs with latest graphics cards, fast processors, and RGB lighting.</p>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• RTX 4070/4080/4090 Graphics</li>
                  <li>• Intel i7/i9 or AMD Ryzen 7/9</li>
                  <li>• 32GB+ DDR5 RAM</li>
                  <li>• 1TB+ NVMe SSD</li>
                  <li>• Custom RGB Lighting</li>
                </ul>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <Briefcase className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-3">Business Workstations</h3>
                <p className="text-muted-foreground mb-4">Reliable systems for office work, data processing, and professional applications.</p>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Professional Graphics</li>
                  <li>• ECC Memory Options</li>
                  <li>• Multiple Display Support</li>
                  <li>• Enterprise Security</li>
                  <li>• Silent Operation</li>
                </ul>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <Palette className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-3">Creative Workstations</h3>
                <p className="text-muted-foreground mb-4">Optimized for video editing, 3D rendering, graphic design, and content creation.</p>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Workstation Graphics Cards</li>
                  <li>• 64GB+ RAM Configurations</li>
                  <li>• Color-Accurate Displays</li>
                  <li>• High-Speed Storage Arrays</li>
                  <li>• Professional Software</li>
                </ul>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="specifications" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-4">Processor Options</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">Intel Options</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Core i5-13600K / i7-13700K / i9-13900K</li>
                      <li>• Core i5-14600K / i7-14700K / i9-14900K</li>
                      <li>• Xeon W-Series for Workstations</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">AMD Options</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Ryzen 5 7600X / Ryzen 7 7700X</li>
                      <li>• Ryzen 9 7900X / 7950X</li>
                      <li>• Threadripper for High-End</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-4">Graphics Cards</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">NVIDIA RTX Series</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• RTX 4060 / 4070 / 4070 Ti</li>
                      <li>• RTX 4080 / 4090</li>
                      <li>• RTX A-Series Professional</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">AMD Radeon</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• RX 7600 XT / 7700 XT</li>
                      <li>• RX 7800 XT / 7900 XTX</li>
                      <li>• Radeon Pro W-Series</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-4">Memory & Storage</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">RAM Options</span>
                    <span>16GB / 32GB / 64GB / 128GB DDR5</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Primary Storage</span>
                    <span>1TB / 2TB / 4TB NVMe Gen4 SSD</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Secondary Storage</span>
                    <span>2TB / 4TB / 8TB SATA SSD/HDD</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Raid Options</span>
                    <span>RAID 0/1/5/10 Configurations</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-4">Cooling & Power</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">CPU Cooling</span>
                    <span>AIO Liquid / Air / Custom Loop</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Case Fans</span>
                    <span>Premium Noctua / be quiet!</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Power Supply</span>
                    <span>80+ Gold/Platinum Modular PSU</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Wattage</span>
                    <span>750W / 850W / 1000W / 1200W</span>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="pricing" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-4">Essential PC</h3>
                <div className="text-2xl font-bold text-primary mb-2">₹55,000</div>
                <p className="text-sm text-muted-foreground mb-4">Perfect for office work and light gaming</p>
                <ul className="space-y-2 text-sm text-muted-foreground mb-6">
                  <li>• Intel i5 / AMD Ryzen 5</li>
                  <li>• 16GB DDR4/DDR5 RAM</li>
                  <li>• 512GB NVMe SSD</li>
                  <li>• RTX 4060 / RX 7600</li>
                  <li>• 2-Year Warranty</li>
                </ul>
                <Button onClick={handleRequestQuote} variant="outline" className="w-full">Request Quote</Button>
              </div>
              
              <div className="bg-card p-6 rounded-lg border ring-2 ring-primary">
                <div className="text-xs bg-primary text-white px-2 py-1 rounded mb-2 inline-block">MOST POPULAR</div>
                <h3 className="text-xl font-semibold mb-4">Performance PC</h3>
                <div className="text-2xl font-bold text-primary mb-2">₹95,000</div>
                <p className="text-sm text-muted-foreground mb-4">High-performance for gaming and creative work</p>
                <ul className="space-y-2 text-sm text-muted-foreground mb-6">
                  <li>• Intel i7 / AMD Ryzen 7</li>
                  <li>• 32GB DDR5 RAM</li>
                  <li>• 1TB NVMe + 2TB SSD</li>
                  <li>• RTX 4070 Ti / RX 7800 XT</li>
                  <li>• 3-Year Warranty</li>
                </ul>
                <Button onClick={handleRequestQuote} className="w-full">Request Quote</Button>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-4">Ultimate PC</h3>
                <div className="text-2xl font-bold text-primary mb-2">₹1,75,000</div>
                <p className="text-sm text-muted-foreground mb-4">No-compromise performance for professionals</p>
                <ul className="space-y-2 text-sm text-muted-foreground mb-6">
                  <li>• Intel i9 / AMD Ryzen 9</li>
                  <li>• 64GB DDR5 RAM</li>
                  <li>• 2TB NVMe + 4TB SSD</li>
                  <li>• RTX 4090 / Professional GPU</li>
                  <li>• 5-Year Warranty</li>
                </ul>
                <Button onClick={handleRequestQuote} variant="outline" className="w-full">Request Quote</Button>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="services" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold mb-4">Build Services</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Code className="h-5 w-5 text-primary mr-3 mt-0.5" />
                    <div>
                      <div className="font-medium">Custom Configuration</div>
                      <div className="text-sm text-muted-foreground">Tailored component selection based on your specific needs and budget</div>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <Monitor className="h-5 w-5 text-primary mr-3 mt-0.5" />
                    <div>
                      <div className="font-medium">Professional Assembly</div>
                      <div className="text-sm text-muted-foreground">Expert assembly with proper cable management and testing</div>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <HardDrive className="h-5 w-5 text-primary mr-3 mt-0.5" />
                    <div>
                      <div className="font-medium">OS & Software Setup</div>
                      <div className="text-sm text-muted-foreground">Windows installation, drivers, and software pre-configuration</div>
                    </div>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-4">Post-Purchase Support</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Award className="h-5 w-5 text-primary mr-3 mt-0.5" />
                    <div>
                      <div className="font-medium">Extended Warranty</div>
                      <div className="text-sm text-muted-foreground">Up to 5-year comprehensive warranty covering all components</div>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <Zap className="h-5 w-5 text-primary mr-3 mt-0.5" />
                    <div>
                      <div className="font-medium">Upgrade Services</div>
                      <div className="text-sm text-muted-foreground">Future-proof your system with professional upgrade installations</div>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <Thermometer className="h-5 w-5 text-primary mr-3 mt-0.5" />
                    <div>
                      <div className="font-medium">Maintenance Plans</div>
                      <div className="text-sm text-muted-foreground">Regular cleaning, thermal paste replacement, and optimization</div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Customer Reviews */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">Customer Testimonials</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400 flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                    </svg>
                  ))}
                </div>
                <span className="ml-2 text-gray-600 text-sm">5.0</span>
              </div>
              <p className="text-gray-700 mb-4">"The custom PC we ordered for our design studio has been perfect. The thoughtful component selection and quiet operation have made a huge difference in our workflow. Render times are incredible!"</p>
              <div className="text-sm">
                <p className="font-medium">Vikram Mehta</p>
                <p className="text-gray-500">Creative Director, Design Studio</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400 flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                    </svg>
                  ))}
                </div>
                <span className="ml-2 text-gray-600 text-sm">5.0</span>
              </div>
              <p className="text-gray-700 mb-4">"After struggling with off-the-shelf PCs, switching to Truecore's custom builds was a game-changer. Our rendering times have been cut in half, and the machines run cool even under load."</p>
              <div className="text-sm">
                <p className="font-medium">Preeti Singh</p>
                <p className="text-gray-500">3D Visualization Expert</p>
              </div>
            </div>
          </div>
        </div>

        {/* Build Process */}
        <div className="mb-16 bg-muted/30 px-8 py-10 rounded-xl">
          <h2 className="text-2xl font-bold mb-8 text-center">Our Build Process</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="bg-primary text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">1</div>
              <h3 className="font-semibold mb-2">Consultation</h3>
              <p className="text-sm text-muted-foreground">Understand your needs, usage patterns, and budget requirements</p>
            </div>
            <div className="text-center">
              <div className="bg-primary text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">2</div>
              <h3 className="font-semibold mb-2">Configuration</h3>
              <p className="text-sm text-muted-foreground">Custom part selection and compatibility testing for optimal performance</p>
            </div>
            <div className="text-center">
              <div className="bg-primary text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">3</div>
              <h3 className="font-semibold mb-2">Assembly</h3>
              <p className="text-sm text-muted-foreground">Professional build with stress testing and quality assurance</p>
            </div>
            <div className="text-center">
              <div className="bg-primary text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">4</div>
              <h3 className="font-semibold mb-2">Delivery</h3>
              <p className="text-sm text-muted-foreground">Secure packaging and delivery with setup support if needed</p>
            </div>
          </div>
        </div>

        {/* Related Products */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">Complete Your Setup</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-card p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-2">Enterprise Desktops</h3>
              <p className="text-muted-foreground mb-4">
                Server-grade desktop solutions for businesses requiring maximum reliability and performance.
              </p>
              <Link to="/products/desktops" className="text-primary flex items-center">
                Explore Desktops
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-2">Smart Accessories</h3>
              <p className="text-muted-foreground mb-4">
                Professional keyboards, mice, monitors, and other peripherals to complete your workstation.
              </p>
              <Link to="/products/smart-gadgets" className="text-primary flex items-center">
                Explore Accessories
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center bg-gradient-to-r from-primary/5 to-secondary/5 p-8 rounded-xl">
          <h2 className="text-3xl font-bold mb-4">Ready to Build Your Dream PC?</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
            Get a personalized quote based on your specific requirements. Our experts will help you build the perfect system within your budget.
          </p>
          <Button 
            onClick={handleRequestQuote}
            size="lg"
            className="text-lg px-8 py-3"
          >
            Get Custom Quote - Free Consultation
            <ArrowRight className="ml-2" />
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default ProductPC;
